import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import ContentSection from '@/components/ContentSection';
import AlbumGrid from '@/components/AlbumGrid';
import { api } from '@/lib/apiClient';
import { Album } from '@/types/interface';
import { useClientMount } from '@/hooks/useClientMount';

export default function Home() {
    const [albums, setAlbums] = useState<Album[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const hasMounted = useClientMount();

    useEffect(() => {
        if (!hasMounted) return;

        const fetchAlbums = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await api.albums.getAll({ isFeatured: true }); // Fetch only featured albums
                const data = response.data;
                console.log(data);
                setAlbums(data.albums || []);
            } catch (err: unknown) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to fetch albums';
                setError(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        fetchAlbums();
    }, [hasMounted]);

    // Don't render anything until the component has mounted on the client
    if (!hasMounted) {
        return null;
    }

    return (
        <Layout>
            <div className="bg-gray-50 min-h-screen">
                {/* Enhanced Hero Section */}
                <div className="relative bg-gray-900 text-gray-50 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-black to-gray-950 opacity-90"></div>
                    <div className="relative max-w-7xl mx-auto px-6 py-24 sm:py-32 text-center">
                        <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 text-gray-50">
                            Featured Albums
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                            Discover stunning photography collections capturing the most precious moments with our beloved pets
                        </p>
                        <div className="mt-8 flex justify-center">
                            <div className="w-24 h-1 bg-gray-300 rounded-full"></div>
                        </div>
                    </div>
                </div>

                {/* Featured Albums Section */}
                <ContentSection maxWidth="7xl" padding="xl" className="relative bg-gray-50">
                    {loading && (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-gray-900 mb-4"></div>
                            <p className="text-gray-600 text-lg">Loading featured albums...</p>
                        </div>
                    )}

                    {error && (
                        <div className="text-center py-16">
                            <div className="inline-flex items-center px-8 py-6 bg-gray-50 border border-gray-200 rounded-2xl shadow-sm">
                                <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-4">
                                    <svg className="w-5 h-5 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-gray-900 font-medium">Unable to load albums</p>
                                    <p className="text-gray-600 text-sm mt-1">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {!loading && !error && albums.length === 0 && (
                        <div className="text-center py-20">
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6">
                                <svg className="w-10 h-10 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-medium text-gray-900 mb-2">No Featured Albums</h3>
                            <p className="text-gray-600 text-lg max-w-md mx-auto">
                                We're working on adding some amazing featured albums. Check back soon!
                            </p>
                        </div>
                    )}

                    {!loading && !error && albums.length > 0 && (
                        <div className="space-y-8">
                            <div className="text-center">
                                <h2 className="text-3xl font-bold text-gray-900 mb-4">Featured Collections</h2>
                                <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                                    Handpicked albums showcasing the most beautiful moments
                                </p>
                            </div>
                            <AlbumGrid albums={albums} isFeatured={true} />
                        </div>
                    )}
                </ContentSection>

                {/* Enhanced Call to Action Section */}
                <div className="relative overflow-hidden">
                    <ContentSection maxWidth="5xl" padding="xl" centered className="relative">
                        <div className="bg-gray-100 rounded-3xl shadow-xl p-12 border border-gray-200">
                            <div className="text-center">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-900 rounded-full mb-6">
                                    <svg className="w-8 h-8 text-gray-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                    </svg>
                                </div>
                                <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                                    Ready to Explore More?
                                </h2>
                                <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
                                    Dive into our complete collection of photography albums and discover even more beautiful moments
                                </p>
                                <Link
                                    to="/albums"
                                    className="group inline-flex items-center gap-3 bg-gray-900 text-gray-50 px-10 py-4 rounded-full hover:bg-gray-800 transition-all duration-300 font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                                >
                                    View All Albums
                                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                    </svg>
                                </Link>
                            </div>
                        </div>
                    </ContentSection>
                </div>
            </div>
        </Layout>
    );
}
